import React, { useState, useCallback, useEffect } from "react";
import { Box, Button, CircularProgress, Collapse, useMediaQuery } from "@mui/material";
import {
  Add as AddIcon,
  BusinessOutlined as EmptyIcon,
} from "@mui/icons-material";
import {
  dt,
  accentGradientDark,
  accentGradientDarkHover,
  accentShimmer,
} from "../../../../shared/themes/redesignTokens";
import { useRedesignSetup } from "../../../../shared/hooks/useRedesignSetup";
import { useAppDispatch, useAppSelector } from "../../../../shared/hooks/sharedHooks";
import {
  fetchLocations,
  createLocation,
  updateLocation,
  disableLocation,
  setDefaultLocation,
} from "../../customerLocationsSlice";
import { invalidateMachines } from "../../customerMachinerySlice";
import LocationRow from "./LocationRow";
import AddLocationDialog from "./AddLocationDialog";
import EditLocationDialog from "./EditLocationDialog";
import type { Location, LocationFormData } from "./locationTypes";

// ═══════════════════════════════════════════════════════════════
//  COMPONENT
// ═══════════════════════════════════════════════════════════════

const LocationsPage: React.FC = () => {
  useRedesignSetup();
  const dispatch = useAppDispatch();

  const isCompact = useMediaQuery("(max-width:1200px)");
  const isPhone = useMediaQuery("(max-width:768px)");

  // Redux state
  const locations = useAppSelector((state) => state.customerLocations.locations);
  const loading = useAppSelector((state) => state.customerLocations.loading);

  // Local UI state
  const [expandedIds, setExpandedIds] = useState<Set<string>>(new Set());
  const [addDialogOpen, setAddDialogOpen] = useState(false);
  const [editLocation, setEditLocation] = useState<Location | null>(null);

  // Fetch locations on mount
  useEffect(() => {
    dispatch(fetchLocations());
  }, [dispatch]);

  // Auto-expand first location when locations load
  useEffect(() => {
    if (locations.length > 0 && expandedIds.size === 0) {
      setExpandedIds(new Set([locations[0].id]));
    }
  }, [locations]); // eslint-disable-line react-hooks/exhaustive-deps

  // Handlers
  const toggleExpand = useCallback((id: string) => {
    setExpandedIds((prev) => {
      const next = new Set(prev);
      if (next.has(id)) next.delete(id);
      else next.add(id);
      return next;
    });
  }, []);

  const handleEdit = useCallback((location: Location) => {
    setEditLocation(location);
  }, []);

  const handleSetDefault = useCallback((location: Location) => {
    dispatch(setDefaultLocation(location.id)).then(() => {
      dispatch(fetchLocations());
      dispatch(invalidateMachines());
    });
  }, [dispatch]);

  const handleDisable = useCallback((location: Location) => {
    dispatch(disableLocation(location.id)).then(() => {
      dispatch(fetchLocations());
      dispatch(invalidateMachines());
    });
  }, [dispatch]);

  const handleAddSave = useCallback((data: LocationFormData) => {
    dispatch(createLocation(data)).then(() => {
      dispatch(fetchLocations());
      dispatch(invalidateMachines());
      setAddDialogOpen(false);
    });
  }, [dispatch]);

  const handleEditSave = useCallback((data: LocationFormData) => {
    if (!editLocation) return;
    dispatch(updateLocation({ id: editLocation.id, data })).then(() => {
      dispatch(fetchLocations());
      dispatch(invalidateMachines());
      setEditLocation(null);
    });
  }, [dispatch, editLocation]);

  // Build flat list of parent locations for dropdown
  const parentOptions = locations.map((loc) => ({
    value: loc.id,
    label: loc.name,
  }));

  return (
    <Box
      sx={{
        display: "flex",
        flexDirection: "column",
        flex: 1,
        backgroundColor: dt.pageBg,
        backgroundImage: `radial-gradient(circle at 1px 1px, ${dt.pageDotColor} 1px, transparent 0)`,
        backgroundSize: "28px 28px",
        fontFamily: dt.font,
        overflowX: "hidden",
        px: isPhone ? "16px" : "32px",
        "& *:not(.MuiSvgIcon-root)": { fontFamily: `${dt.font}` },
      }}
    >
      {/* ═══════════════════════════════════════════════════════
          PAGE HEADER
          ═══════════════════════════════════════════════════════ */}
      <Box
        sx={{
          display: "flex",
          alignItems: "flex-start",
          justifyContent: "space-between",
          flexWrap: "wrap",
          gap: "12px",
          pt: isPhone ? 2 : 3,
          pb: 2,
          animation: "fadeUp 0.4s cubic-bezier(0.4, 0, 0.2, 1) both",
        }}
      >
        <Box>
          <Box
            sx={{
              fontSize: "11px",
              fontWeight: 600,
              textTransform: "uppercase",
              letterSpacing: "1.2px",
              color: dt.textMuted,
              mb: 0.5,
            }}
          >
            COMPANY SETTINGS
          </Box>
          <Box
            sx={{
              fontSize: isPhone ? "22px" : "28px",
              fontWeight: 700,
              color: dt.textPrimary,
              lineHeight: 1.2,
              position: "relative",
              "&::after": {
                content: '""',
                position: "absolute",
                bottom: -4,
                left: 0,
                width: "40px",
                height: "3px",
                borderRadius: "2px",
                background: accentShimmer,
              },
            }}
          >
            Locations
          </Box>
        </Box>

        <Button
          variant="contained"
          startIcon={<AddIcon sx={{ fontSize: "16px !important" }} />}
          onClick={() => setAddDialogOpen(true)}
          sx={{
            background: accentGradientDark,
            borderRadius: dt.radiusPill,
            padding: isPhone ? "6px 14px" : "8px 20px",
            textTransform: "none",
            fontWeight: 600,
            fontSize: isPhone ? "12px" : "13px",
            fontFamily: dt.font,
            letterSpacing: "0.2px",
            boxShadow: "0 2px 8px rgba(52,152,219,0.25)",
            border: "none",
            whiteSpace: "nowrap",
            flexShrink: 0,
            lineHeight: 1.5,
            transition: "all 0.3s cubic-bezier(0.4, 0, 0.2, 1)",
            "&:hover": {
              background: accentGradientDarkHover,
              transform: "translateY(-1px)",
              boxShadow: "0 4px 12px rgba(52,152,219,0.3)",
            },
            "&:active": {
              transform: "translateY(0)",
              boxShadow: "0 2px 8px rgba(52,152,219,0.25)",
            },
          }}
        >
          Add Location
        </Button>
      </Box>

      {/* ═══════════════════════════════════════════════════════
          LOCATION LIST
          ═══════════════════════════════════════════════════════ */}
      <Box
        sx={{
          display: "flex",
          flexDirection: "column",
          gap: "12px",
          pb: 4,
          animation: "fadeUp 0.5s cubic-bezier(0.4, 0, 0.2, 1) 0.1s both",
        }}
      >
        {loading && locations.length === 0 ? (
          <Box sx={{ display: "flex", justifyContent: "center", py: 8 }}>
            <CircularProgress size={32} sx={{ color: dt.blue }} />
          </Box>
        ) : locations.length === 0 ? (
          <Box
            sx={{
              backgroundColor: dt.cardBg,
              borderRadius: dt.radius,
              border: `1px solid ${dt.cardBorder}`,
              boxShadow: dt.cardShadow,
              p: 6,
              display: "flex",
              flexDirection: "column",
              alignItems: "center",
              justifyContent: "center",
              textAlign: "center",
              gap: 2,
            }}
          >
            <EmptyIcon sx={{ fontSize: 48, color: dt.textMuted, opacity: 0.5 }} />
            <Box sx={{ fontSize: "16px", fontWeight: 600, color: dt.textSecondary }}>
              No locations yet
            </Box>
            <Box sx={{ fontSize: "13px", color: dt.textMuted, maxWidth: 320 }}>
              Add your first location to organize machines by facility, building, or hall.
            </Box>
          </Box>
        ) : (
          locations.map((parentLocation, index) => (
            <LocationCard
              key={parentLocation.id}
              location={parentLocation}
              expanded={expandedIds.has(parentLocation.id)}
              onToggleExpand={() => toggleExpand(parentLocation.id)}
              onEdit={handleEdit}
              onSetDefault={handleSetDefault}
              onDisable={handleDisable}
              animationDelay={index * 0.06}
            />
          ))
        )}
      </Box>

      {/* ═══════════════════════════════════════════════════════
          DIALOGS
          ═══════════════════════════════════════════════════════ */}
      <AddLocationDialog
        open={addDialogOpen}
        onClose={() => setAddDialogOpen(false)}
        onSave={handleAddSave}
        parentOptions={parentOptions}
      />

      {editLocation && (
        <EditLocationDialog
          open={!!editLocation}
          onClose={() => setEditLocation(null)}
          onSave={handleEditSave}
          location={editLocation}
          parentOptions={parentOptions.filter((p) => p.value !== editLocation.id)}
        />
      )}
    </Box>
  );
};

// ═══════════════════════════════════════════════════════════════
//  LOCATION CARD (parent with collapsible children)
// ═══════════════════════════════════════════════════════════════

interface LocationCardProps {
  location: Location;
  expanded: boolean;
  onToggleExpand: () => void;
  onEdit: (location: Location) => void;
  onSetDefault: (location: Location) => void;
  onDisable: (location: Location) => void;
  animationDelay: number;
}

const LocationCard: React.FC<LocationCardProps> = ({
  location,
  expanded,
  onToggleExpand,
  onEdit,
  onSetDefault,
  onDisable,
  animationDelay,
}) => {
  const hasChildren = location.childLocations && location.childLocations.length > 0;

  return (
    <Box
      sx={{
        backgroundColor: dt.cardBg,
        borderRadius: dt.radius,
        border: `1px solid ${dt.cardBorder}`,
        boxShadow: dt.cardShadow,
        overflow: "hidden",
        transition: "box-shadow 0.2s ease",
        animation: `cardReveal 0.4s cubic-bezier(0.4, 0, 0.2, 1) ${animationDelay}s both`,
        "&:hover": {
          boxShadow: dt.cardShadowHover,
        },
      }}
    >
      {/* Parent row */}
      <LocationRow
        location={location}
        expanded={expanded}
        onToggleExpand={onToggleExpand}
        onEdit={onEdit}
        onSetDefault={onSetDefault}
        onDisable={onDisable}
      />

      {/* Child rows (collapsible) */}
      {hasChildren && (
        <Collapse in={expanded} timeout={250}>
          <Box
            sx={{
              borderTop: `1px solid ${dt.rowBorder}`,
              backgroundColor: dt.headerBg,
            }}
          >
            {location.childLocations.map((child) => (
              <LocationRow
                key={child.id}
                location={child}
                isChild
                onEdit={onEdit}
                onDisable={onDisable}
              />
            ))}
          </Box>
        </Collapse>
      )}
    </Box>
  );
};

export default LocationsPage;
